import { updateStoreProductPrice } from '@gozoki/api';
import { Button, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../components/communication/Toast';

interface StorePricingValues {
    sellPrice?: number;
    franco?: number;
    coldStartStockMin?: number;
    agPromo?: number;
}

interface StorePricingGridRowProps {
    storeId: number;
    productReference: string;
    label: string;
    pricingValues: StorePricingValues;
    edit: boolean;
    disabled: boolean;
    onModify: (reference: string) => void;
    onSave: () => void;
    onCancel: () => void;
}

const formatPrice = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return value.toFixed(2);
};

const formatPercent = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return value.toString();
};

const formatCount = (value?: number) => {
    if (value === undefined || value === null) return '-';
    return value.toString();
};

export const StorePricingGridRow = (props: StorePricingGridRowProps) => {
    const {
        storeId,
        productReference,
        label,
        pricingValues,
        edit,
        disabled,
        onModify,
        onSave,
        onCancel,
    } = props;
    const [Toast, showToast] = useToast();

    const [editValues, setEditValues] = useState<StorePricingValues>(pricingValues);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setEditValues(pricingValues);
    }, [pricingValues]);

    const handleInputChange = useCallback((field: keyof StorePricingValues, value: string) => {
        const numValue = value === '' ? undefined : Number(value);
        setEditValues((prev) => ({
            ...prev,
            [field]: numValue,
        }));
    }, []);

    const handleSave = useCallback(async () => {
        try {
            setLoading(true);
            const apiValues = Object.entries(editValues).reduce((acc, [key, value]) => {
                if (value !== undefined) {
                    acc[key as keyof StorePricingValues] = value;
                }
                return acc;
            }, {} as StorePricingValues);

            await updateStoreProductPrice(storeId, productReference, apiValues);
            onSave();
            showToast({
                severity: 'success',
                message: 'Prix mis à jour avec succès',
            });
        } catch (error) {
            showToast({
                severity: 'error',
                message: 'Erreur lors de la mise à jour des prix',
            });
        } finally {
            setLoading(false);
        }
    }, [editValues, onSave, showToast, storeId, productReference]);

    const handleCancel = useCallback(() => {
        setEditValues(pricingValues);
        onCancel();
    }, [pricingValues, onCancel]);

    if (loading && !edit) {
        return (
            <Stack flexDirection="row" gap={1} alignItems="center" py={1}>
                <Typography flex={2} px={1}>
                    {label}
                </Typography>
                <Typography flex={4} textAlign="center">
                    Chargement...
                </Typography>
            </Stack>
        );
    }

    return (
        <>
            <Toast />
            <Stack flexDirection="row" gap={1} alignItems="center" py={1}>
                <Typography flex={2} px={1}>
                    {label}
                </Typography>

                <Stack flex={1}>
                    {edit ? (
                        <TextField
                            size="small"
                            type="number"
                            value={editValues.sellPrice || ''}
                            onChange={(e) => handleInputChange('sellPrice', e.target.value)}
                            inputProps={{ step: 0.1, min: 0 }}
                        />
                    ) : (
                        <Typography>{formatPrice(pricingValues.sellPrice)}</Typography>
                    )}
                </Stack>

                <Stack flex={1}>
                    {edit ? (
                        <TextField
                            size="small"
                            type="number"
                            value={editValues.franco || ''}
                            onChange={(e) => handleInputChange('franco', e.target.value)}
                            inputProps={{ step: 1, min: 0 }}
                        />
                    ) : (
                        <Typography>{formatCount(pricingValues.franco)}</Typography>
                    )}
                </Stack>

                <Stack flex={1}>
                    {edit ? (
                        <TextField
                            size="small"
                            type="number"
                            value={editValues.coldStartStockMin || ''}
                            onChange={(e) => handleInputChange('coldStartStockMin', e.target.value)}
                            inputProps={{ step: 1, min: 0 }}
                        />
                    ) : (
                        <Typography>{formatCount(pricingValues.coldStartStockMin)}</Typography>
                    )}
                </Stack>

                <Stack flex={1}>
                    {edit ? (
                        <TextField
                            size="small"
                            type="number"
                            value={editValues.agPromo || ''}
                            onChange={(e) => handleInputChange('agPromo', e.target.value)}
                            inputProps={{ step: 1, min: 0, max: 100 }}
                        />
                    ) : (
                        <Typography>{formatPercent(pricingValues.agPromo)}</Typography>
                    )}
                </Stack>

                <Stack flex={1} flexDirection="row" gap={1}>
                    {edit ? (
                        <>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleSave}
                                disabled={loading}
                            >
                                Sauvegarder
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleCancel}
                                disabled={loading}
                            >
                                Annuler
                            </Button>
                        </>
                    ) : (
                        <Button
                            size="small"
                            variant="outlined"
                            disabled={disabled}
                            onClick={() => onModify(productReference)}
                        >
                            Modifier
                        </Button>
                    )}
                </Stack>
            </Stack>
        </>
    );
};
